import { useEffect, useRef, useState } from 'react';
import loadGoogleMaps from '../../util/loadGoogleMaps.js';
import { loadClassicScript } from '../../util/loadClassicScript.js';
import projectedOverlayUrl from '../../util/ProjectedOverlay.js?url';
import geoxml3Url from '../../util/geoxml3.js?url';

const DEFAULT_MAP_OPTIONS = {
	center: { lat: -34.397, lng: 150.644 },
	zoom: 8,
	zoomControl: true,
	rotateControl: true,
	streetViewControl: false,
	fullscreenControl: false,
	mapTypeId: 'roadmap'
};

function waitForContainerLayout(el) {
	if (!el) {
		return Promise.resolve();
	}
	if (el.clientHeight > 0 && el.clientWidth > 0) {
		return Promise.resolve();
	}
	// Two RAFs: allow parent % / absolute layout to settle after mount.
	return new Promise(resolve => {
		requestAnimationFrame(() => {
			requestAnimationFrame(resolve);
		});
	});
}

/**
 * Creates a google.maps.Map once on mount and keeps it on a ref.
 * Loads the Maps JS API plus ProjectedOverlay / geoxml3 as classic scripts
 * (they depend on non-strict implicit globals — ESM import() breaks KML parse).
 * Lifecycle: create on mount, clear ref on unmount (no recreate on parent re-renders).
 *
 * @param {React.RefObject<HTMLElement|null>} containerRef DOM node for the map
 * @returns {{ mapRef: React.MutableRefObject<google.maps.Map|null>, ready: boolean }}
 */
export default function useGoogleMap(containerRef) {
	const mapRef = useRef(null);
	const [ready, setReady] = useState(false);

	useEffect(() => {
		let cancelled = false;
		let map = null;
		let resizeObserver = null;
		let idleListener = null;

		const triggerResize = () => {
			if (!map || !window.google || !google.maps || !google.maps.event) {
				return;
			}
			google.maps.event.trigger(map, 'resize');
		};

		(async () => {
			try {
				await loadGoogleMaps();
				// Require window.google.maps at classic-script evaluation time.
				await loadClassicScript(
					projectedOverlayUrl,
					() => typeof window.ProjectedOverlay === 'function'
				);
				await loadClassicScript(
					geoxml3Url,
					() => !!(window.geoXML3 && typeof window.geoXML3.parser === 'function')
				);
			} catch (err) {
				console.error(err);
				return;
			}

			if (cancelled || !containerRef.current) {
				return;
			}

			await waitForContainerLayout(containerRef.current);
			if (cancelled || !containerRef.current) {
				return;
			}

			// Drop any leftover Maps DOM from a prior mount (HMR / remount).
			containerRef.current.innerHTML = '';

			map = new google.maps.Map(containerRef.current, {
				...DEFAULT_MAP_OPTIONS,
				mapTypeControlOptions: {
					position: google.maps.ControlPosition.BOTTOM_RIGHT
				},
				zoomControlOptions: {
					position: google.maps.ControlPosition.RIGHT_BOTTOM
				},
				rotateControlOptions: {
					position: google.maps.ControlPosition.BOTTOM_RIGHT
				}
			});

			mapRef.current = map;

			idleListener = google.maps.event.addListenerOnce(map, 'idle', triggerResize);
			requestAnimationFrame(triggerResize);

			if (typeof ResizeObserver !== 'undefined') {
				resizeObserver = new ResizeObserver(() => {
					triggerResize();
				});
				resizeObserver.observe(containerRef.current);
			}

			if (!cancelled) {
				setReady(true);
			}
		})();

		return () => {
			cancelled = true;
			if (resizeObserver) {
				resizeObserver.disconnect();
				resizeObserver = null;
			}
			if (idleListener && window.google && google.maps && google.maps.event) {
				google.maps.event.removeListener(idleListener);
			}
			mapRef.current = null;
			map = null;
			if (containerRef.current) {
				containerRef.current.innerHTML = '';
			}
			setReady(false);
		};
	}, [containerRef]);

	return { mapRef, ready };
}
